import { Injectable } from '@angular/core';
import { ApiResourceService } from "libraries/service/apiResource/apiResource.service";
import { RequestForLocationSearch, ResponseForLocationSearch } from "@afidium/ts-facade";
import { Observable, of } from "rxjs";
import { IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { flatMap } from "rxjs/internal/operators";
import _cloneDeep from "lodash-es/cloneDeep";
import { tap } from "rxjs/operators";

const DICTIONARY_SEARCH: string = 'dictionary_search';
const LOCATION: string = 'location';
const LOCATION_COUNTRY: string = 'location_country';
const LOCATION_STATE: string = 'location_state';
const DICTIONARY: string = 'dictionary';
const DICTIONARIES: string = 'dictionaries';
const SEARCH: string = 'search';
const LIST: string = 'list';

@Injectable()
export class DictionarySearchResourceService {

    private countryListCache: IMultiselectOptions[];
    private stateListCache: IMultiselectOptions[];
    private locationCache: Map<string, ResponseForLocationSearch> = new Map<string, ResponseForLocationSearch>();

    constructor(
        private readonly apiResourceService: ApiResourceService,
    ) {
    }

    public locationSearch(request: RequestForLocationSearch): Observable<ResponseForLocationSearch> {
        const key: string = this.locationKeyMaker(request);
        if(!this.locationCache.has(key)){
            return this.apiResourceService.postOne<RequestForLocationSearch, ResponseForLocationSearch>(
                this.apiResourceService.getApiUrl(DICTIONARY_SEARCH, LOCATION, SEARCH),
                request
            )
                .pipe(
                    tap((response: ResponseForLocationSearch): void => {
                        this.locationCache.set(key, response);
                    })
                );
        }
        return of(this.locationCache.get(key));
    }

    public getCountryList(): Observable<IMultiselectOptions[]> {

        const obs: Observable<IMultiselectOptions[]> = this.countryListCache ?
            of(this.countryListCache) :
            this.apiResourceService.get<IMultiselectOptions>(
                this.apiResourceService.getApiUrl(DICTIONARY, DICTIONARIES, LOCATION_COUNTRY, LIST),
            );


        return obs
            .pipe(
                flatMap((list: IMultiselectOptions[]): Observable<IMultiselectOptions[]> => {
                    if (!this.countryListCache) {
                        const transformedList: IMultiselectOptions[] = list
                            .map((item: IMultiselectOptions): IMultiselectOptions => {
                                return {
                                    ...item,
                                    // @ts-ignore
                                    value: item.code,
                                    // @ts-ignore
                                    label: item.name,
                                };
                            })
                            .sort((a: IMultiselectOptions, b: IMultiselectOptions): number => {
                                const aLabel: string = a.label === 'France' ? 'aaaaaaaaaaa' : a.label;
                                const bLabel: string = b.label === 'France' ? 'aaaaaaaaaaa' : b.label;
                                return aLabel.localeCompare(bLabel);
                            });
                        this.countryListCache = transformedList;
                    }
                    return of(this.countryListCache);
                })
            );
    }

    public getStateList(): Observable<IMultiselectOptions[]> {
        const obs: Observable<IMultiselectOptions[]> = this.stateListCache ?
            of(this.stateListCache) :
            this.apiResourceService.get<IMultiselectOptions>(
                this.apiResourceService.getApiUrl(DICTIONARY, DICTIONARIES, LOCATION_STATE, LIST),
            );

        return obs
            .pipe(
                flatMap((list: IMultiselectOptions[]): Observable<IMultiselectOptions[]> => {
                    if (!this.stateListCache) {
                        this.stateListCache = _cloneDeep(list);
                    }
                    return of(this.stateListCache);
                })
            );
    }

    public searchCountry(query: string): Observable<IMultiselectOptions[]> {
        if (!query?.length) {
            return of([]);
        }
        return this.getCountryList()
            .pipe(
                flatMap((list: IMultiselectOptions[]): Observable<IMultiselectOptions[]> => {
                    if (query?.length === 2) {
                        const foundItem: IMultiselectOptions = list.find((item: IMultiselectOptions): boolean => (item.value as string).toLowerCase() === query.toLowerCase());
                        if (foundItem) {
                            return of([foundItem]);
                        }
                    } else if (query?.length) {
                        const filteredList: IMultiselectOptions[] = list
                            .filter((item: IMultiselectOptions): boolean => (item.label as string).toLowerCase().includes(query.toLowerCase()));
                        return of(filteredList);
                    }
                    return of(list);
                })
            );
    }

    private locationKeyMaker(request: RequestForLocationSearch): string {
        return `${request.query}_${request.locationTypeArray?.length ? request.locationTypeArray.join('') : 'all'}`;
    }
}


